import React, { useEffect, useState } from "react";
import { Sun, Moon } from "@phosphor-icons/react";

// Sprint 63 — light / dark theme. Default dark (brand) unless the OS prefers
// light and the visitor never chose. Persisted per browser in `wp_theme`.
const KEY = "wp_theme";

export function readTheme() {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch { /* private mode */ }
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
  } catch { /* noop */ }
  return "dark";
}

export function applyTheme(theme) {
  const root = document.documentElement;
  root.classList.toggle("light", theme === "light");
  root.classList.toggle("dark", theme !== "light");
  root.style.colorScheme = theme === "light" ? "light" : "dark";
}

// Reactive theme reader for components that compute colors in JS (ticker glows, charts).
export function useTheme() {
  const [light, setLight] = useState(() => document.documentElement.classList.contains("light"));
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setLight(el.classList.contains("light")));
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return light ? "light" : "dark";
}

export default function ThemeToggle({ className = "" }) {
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => { applyTheme(theme); }, [theme]);
  const flip = () => {
    const next = theme === "light" ? "dark" : "light";
    try { localStorage.setItem(KEY, next); } catch { /* private mode */ }
    setTheme(next);
  };
  const light = theme === "light";
  return (
    <button
      type="button"
      onClick={flip}
      data-testid="theme-toggle"
      aria-label={light ? "Switch to dark mode" : "Switch to light mode"}
      aria-pressed={light}
      title={light ? "Dark mode" : "Light mode"}
      className={`inline-flex items-center justify-center w-9 h-9 border border-zinc-800 hover:border-[#39FF14] text-zinc-400 hover:text-[#39FF14] transition-colors ${className}`}
    >
      {light ? <Moon size={16} weight="fill" /> : <Sun size={16} weight="fill" />}
    </button>
  );
}
